import { useState, useEffect } from 'react'
import { API_CONFIG } from '../config/settings'

export interface RoomDetailsData {
  id: number
  room_id: string
  title: string
  slug: string
  summary: string
  description: string
  rent: number
  bed: number
  bath: number
  max_guests: number
  featured_img: string
  slider_imgs: string
  images: Array<{
    id: number
    url: string
    created_at: string | null
    is_featured?: boolean
  }>
  address: string
  address_area?: string
  address_full_hidden?: boolean
  phone: string
  email: string
  latitude: string
  longitude: string
  listing_status_label?: string
  price_hint?: string
  maps?: {
    google_maps_url?: string
    street_view_url?: string
    plus_code?: string
    maps_embed_url?: string
  }
  is_featured: boolean
  quantity: number
  vendor: {
    id: number
    username: string
    name: string
    email: string
    phone: string
  } | null
  category: {
    id: number
    name: string
    slug: string
  } | null
  rating: number
  reviews_count: number
  reviews: Array<{
    id: number | string
    rating: number
    comment: string
    user_name: string
    user_email: string
    source?: string | null
    created_at: string | null
  }>
  amenities: Array<{
    id: number
    name: string
    serial_number: number
  }>
  availability: {
    available: boolean
    message: string
  }
  meta_keywords: string
  meta_description: string
  created_at: string | null
  updated_at: string | null
}

export const useRoomDetails = (roomId: string, checkin?: string, checkout?: string, guests?: string) => {
  const [roomData, setRoomData] = useState<RoomDetailsData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const fetchRoomDetails = async () => {
      try {
        setLoading(true)
        setError(null)

        // Build query parameters
        const params = new URLSearchParams()
        if (checkin) params.append('checkin', checkin)
        if (checkout) params.append('checkout', checkout)
        if (guests) params.append('guests', guests)

        const queryString = params.toString()
        const url = `${API_CONFIG.BASE_URL}/rooms/${roomId}${queryString ? `?${queryString}` : ''}`

        const response = await fetch(url)
        
        if (!response.ok) {
          throw new Error(`Failed to fetch room details: ${response.statusText}`)
        }

        const data = await response.json()
        
        // Process images: parse slider_imgs and build images array
        let processedImages: Array<{
          id: number
          url: string
          created_at: string | null
          is_featured?: boolean
        }> = []
        
        // Parse slider_imgs if it's a JSON string
        if (data.slider_imgs) {
          try {
            const sliderImgs = typeof data.slider_imgs === 'string' 
              ? JSON.parse(data.slider_imgs) 
              : data.slider_imgs
            
            if (Array.isArray(sliderImgs)) {
              processedImages = sliderImgs.map((url: string, index: number) => ({
                id: index + 1,
                url: url,
                created_at: null,
                is_featured: false
              }))
            }
          } catch (e) {
            console.warn('Failed to parse slider_imgs:', e)
          }
        }
        
        // Add featured image as first image if available
        if (data.featured_img) {
          processedImages.unshift({
            id: 0,
            url: data.featured_img,
            created_at: null,
            is_featured: true
          })
        }
        
        // Use processed images if available, otherwise use provided images array
        if (processedImages.length > 0) {
          data.images = processedImages
        } else if (!data.images || data.images.length === 0) {
          // Fallback: create images array from featured_img
          data.images = data.featured_img ? [{
            id: 0,
            url: data.featured_img,
            created_at: null,
            is_featured: true
          }] : []
        }
        
        setRoomData(data)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch room details')
        console.error('Error fetching room details:', err)
      } finally {
        setLoading(false)
      }
    }

    if (roomId) {
      fetchRoomDetails()
    }
  }, [roomId, checkin, checkout, guests])

  return {
    roomData,
    loading,
    error,
    refetch: () => {
      if (roomId) {
        const fetchRoomDetails = async () => {
          try {
            setLoading(true)
            setError(null)

            const params = new URLSearchParams()
            if (checkin) params.append('checkin', checkin)
            if (checkout) params.append('checkout', checkout)
            if (guests) params.append('guests', guests)

            const queryString = params.toString()
            const url = `${API_CONFIG.BASE_URL}/rooms/${roomId}${queryString ? `?${queryString}` : ''}`

            const response = await fetch(url)
            
            if (!response.ok) {
              throw new Error(`Failed to fetch room details: ${response.statusText}`)
            }

            const data = await response.json()
            
            // Process images: parse slider_imgs and build images array
            let processedImages: Array<{
              id: number
              url: string
              created_at: string | null
              is_featured?: boolean
            }> = []
            
            // Parse slider_imgs if it's a JSON string
            if (data.slider_imgs) {
              try {
                const sliderImgs = typeof data.slider_imgs === 'string' 
                  ? JSON.parse(data.slider_imgs) 
                  : data.slider_imgs
                
                if (Array.isArray(sliderImgs)) {
                  processedImages = sliderImgs.map((url: string, index: number) => ({
                    id: index + 1,
                    url: url,
                    created_at: null,
                    is_featured: false
                  }))
                }
              } catch (e) {
                console.warn('Failed to parse slider_imgs:', e)
              }
            }
            
            // Add featured image as first image if available
            if (data.featured_img) {
              processedImages.unshift({
                id: 0,
                url: data.featured_img,
                created_at: null,
                is_featured: true
              })
            }
            
            // Use processed images if available, otherwise use provided images array
            if (processedImages.length > 0) {
              data.images = processedImages
            } else if (!data.images || data.images.length === 0) {
              // Fallback: create images array from featured_img
              data.images = data.featured_img ? [{
                id: 0,
                url: data.featured_img,
                created_at: null,
                is_featured: true
              }] : []
            }
            
            setRoomData(data)
          } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to fetch room details')
            console.error('Error fetching room details:', err)
          } finally {
            setLoading(false)
          }
        }
        fetchRoomDetails()
      }
    }
  }
}
